import React from 'react';

import type { EditingMode } from '@aline/common';
import {
  isDynamicBlockElement,
  isListItem,
  isParagraphElement,
  isTableCellElement,
  isTableElement,
  nodeIsSelBoundary,
} from '@aline/common';
import {
  ArrowDownToLine,
  ArrowUpToLine,
  Grid2x2X,
  ListOrdered,
  ListRestart,
  ListX,
  Plus,
  RefreshCw,
  TableProperties,
  Trash2,
} from 'lucide-react';
import { equals } from 'ramda';
import type { Path } from 'slate';
import { Editor } from 'slate';
import { DECLARATIVE_MENU_SEPARATOR } from 'src/components/declarative-menu';
import type { DeclarativeMenuConfig } from 'src/components/declarative-menu';
import { DeleteTableColumnsIcon } from 'src/components/icons/DeleteTableColumnsIcon';
import { DeleteTableRowsIcon } from 'src/components/icons/DeleteTableRowsIcon';
import { InsertTableColumnsLeftIcon } from 'src/components/icons/InsertTableColumnsLeftIcon';
import { InsertTableColumnsRightIcon } from 'src/components/icons/InsertTableColumnsRightIcon';
import { InsertTableRowsAboveIcon } from 'src/components/icons/InsertTableRowsAboveIcon';
import { InsertTableRowsBelowIcon } from 'src/components/icons/InsertTableRowsBelowIcon';
import { PageSizeIcon } from 'src/components/icons/PageSizeIcon';
import { Queries } from 'src/editor/queries';
import {
  useMakeTableRectangularAction,
  useTableCellMergeSplitActions,
} from 'src/editor/toolbars/tabs/table-tab';
import { tablePropertiesDialogOpenVar } from 'src/editor/toolbars/ToolbarMenus';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading, useSlateSelector } from 'src/layout-engine';
import { useEditingDisabled, useEditingMode } from 'src/store/document/selectors';

import { useIfEditableMixin } from '../../shared/menu-mixins';
import { useClipboardMenu } from '../use-edit-menu';

interface AuthorOptions {
  authorId: string;
  editingMode: EditingMode;
}

/** The items of the menu shown when right-clicking in the document. */
export function useDocumentContextMenu(): DeclarativeMenuConfig {
  const editor = useEditor();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const editingDisabled = useEditingDisabled();
  const editorLoading = useEditorLoading();
  const clipboardMenu = useClipboardMenu();
  const ifEditable = useIfEditableMixin();
  const tableActions = [...useTableCellMergeSplitActions(), useMakeTableRectangularAction()];
  const {
    canRemoveParagraph,
    insertParagraphAt,
    isInTable,
    isListItemSelected,
    isNestedTable,
    numberOfColumns,
    numberOfRows,
    showRemoveParagraph,
  } = useSlateSelector(getContextMenuState, equals);

  /** The table items stay usable while editing is disabled, but not while the editor loads. */
  const tableItem = { enabled: !editorLoading };

  return [
    ...clipboardMenu,
    {
      icon: <ListOrdered />,
      label: 'Continue numbering',
      onClick: () => runAsAuthor(options => AuthorizedTransforms.continueList(editor, options)),
      visible: isListItemSelected,
      ...ifEditable,
    },
    {
      icon: <ListRestart />,
      label: 'Restart numbering',
      onClick: () => runAsAuthor(options => AuthorizedTransforms.restartList(editor, options)),
      visible: isListItemSelected,
      ...ifEditable,
    },
    {
      icon: <RefreshCw />,
      label: 'Update references',
      onClick: () =>
        runAsAuthor(options => AuthorizedTransforms.updateAllCrossReferences(editor, options)),
      ...ifEditable,
    },
    {
      enabled: !editingDisabled && canRemoveParagraph,
      icon: <ListX />,
      label: 'Remove paragraph',
      onClick: () => runAsAuthor(() => AuthorizedTransforms.removeParagraphAboveSelection(editor)),
      visible: showRemoveParagraph,
    },
    {
      icon: <ArrowUpToLine />,
      label: 'Insert paragraph above',
      onClick: () => insertParagraph('above'),
      visible: Boolean(insertParagraphAt),
      ...ifEditable,
    },
    {
      icon: <ArrowDownToLine />,
      label: 'Insert paragraph below',
      onClick: () => insertParagraph('below'),
      visible: Boolean(insertParagraphAt),
      ...ifEditable,
    },
    ...(isInTable ? getTableMenu() : []),
  ];

  function getTableMenu(): DeclarativeMenuConfig {
    return [
      DECLARATIVE_MENU_SEPARATOR,
      {
        icon: <Plus />,
        label: 'Insert',
        submenu: [
          {
            icon: <InsertTableRowsAboveIcon />,
            label: numberOfRows > 1 ? `Insert ${numberOfRows} rows above` : 'Insert row above',
            onClick: () => insertTableRows('above'),
            ...tableItem,
          },
          {
            icon: <InsertTableRowsBelowIcon />,
            label: numberOfRows > 1 ? `Insert ${numberOfRows} rows below` : 'Insert row below',
            onClick: () => insertTableRows('below'),
            ...tableItem,
          },
          {
            icon: <InsertTableColumnsLeftIcon />,
            label:
              numberOfColumns > 1 ? `Insert ${numberOfColumns} columns left` : 'Insert column left',
            onClick: () => insertTableColumns('left'),
            ...tableItem,
          },
          {
            icon: <InsertTableColumnsRightIcon />,
            label:
              numberOfColumns > 1
                ? `Insert ${numberOfColumns} columns right`
                : 'Insert column right',
            onClick: () => insertTableColumns('right'),
            ...tableItem,
          },
        ],
        ...tableItem,
      },
      {
        icon: <Trash2 />,
        label: 'Delete',
        submenu: [
          {
            icon: <DeleteTableRowsIcon />,
            label: numberOfRows > 1 ? `Delete ${numberOfRows} rows` : 'Delete row',
            onClick: () =>
              runAsAuthor(options => AuthorizedTransforms.deleteTableRows(editor, options)),
            ...tableItem,
          },
          {
            icon: <DeleteTableColumnsIcon />,
            label: numberOfColumns > 1 ? `Delete ${numberOfColumns} columns` : 'Delete column',
            onClick: () =>
              runAsAuthor(options => AuthorizedTransforms.deleteTableColumns(editor, options)),
            ...tableItem,
          },
          {
            icon: <Grid2x2X />,
            label: 'Delete table',
            onClick: () =>
              runAsAuthor(options => AuthorizedTransforms.deleteTable(editor, options)),
            ...tableItem,
          },
        ],
        ...tableItem,
      },
      DECLARATIVE_MENU_SEPARATOR,
      ...tableActions.map(({ enabled, icon: Icon, label, onClick }) => ({
        icon: <Icon />,
        label,
        onClick,
        /** Table actions the selection doesn't allow are left out rather than disabled. */
        visible: enabled,
      })),
      DECLARATIVE_MENU_SEPARATOR,
      {
        icon: <PageSizeIcon />,
        label: isNestedTable ? 'Fit table to cell' : 'Fit table to page',
        onClick: () =>
          runAsAuthor(options => AuthorizedTransforms.fitTableToContainer(editor, options)),
        ...tableItem,
      },
      {
        icon: <TableProperties />,
        label: 'Table properties',
        onClick: () => tablePropertiesDialogOpenVar(true),
        ...tableItem,
      },
    ];
  }

  /** Run an authored transform as the current user, then return focus to the editor. */
  function runAsAuthor(fn: (options: AuthorOptions) => void): void {
    if (!editingMode || !currentUserId) return;

    fn({ authorId: currentUserId, editingMode });
    ClientTransforms.focus(editor);
  }

  function insertParagraph(direction: 'above' | 'below'): void {
    if (!insertParagraphAt) return;

    runAsAuthor(options =>
      AuthorizedTransforms.insertParagraph(editor, { ...options, at: insertParagraphAt, direction })
    );
  }

  function insertTableRows(rowLocation: 'above' | 'below'): void {
    runAsAuthor(options =>
      AuthorizedTransforms.insertTableRows(editor, { ...options, rowLocation })
    );
  }

  function insertTableColumns(columnLocation: 'left' | 'right'): void {
    runAsAuthor(options =>
      AuthorizedTransforms.insertTableColumns(editor, { ...options, columnLocation })
    );
  }
}

function getContextMenuState(editor: Editor): {
  canRemoveParagraph: boolean;
  insertParagraphAt: Path | null;
  isInTable: boolean;
  isListItemSelected: boolean;
  isNestedTable: boolean;
  numberOfColumns: number;
  numberOfRows: number;
  showRemoveParagraph: boolean;
} {
  const insertParagraphEntry = Editor.above(editor, {
    match: n => isTableElement(n) || isDynamicBlockElement(n),
    voids: true,
  });
  const removeParagraphAncestorEntry = Editor.above(editor, {
    match: n => nodeIsSelBoundary(n) || isDynamicBlockElement(n) || isTableCellElement(n),
    voids: true,
  });
  const hasParagraphAboveSelection = Boolean(Editor.above(editor, { match: isParagraphElement }));
  const isLastBlockInAncestor = removeParagraphAncestorEntry?.[0].children.length === 1;
  const isListItemSelected = Boolean(Editor.nodes(editor, { match: isListItem }).next().value);

  const tableState = editor.selection
    ? {
        numberOfColumns: Queries.getContiguousTableCells(editor).length,
        numberOfRows: Queries.getContiguousTableRows(editor).rowEntries.length,
        ...Queries.getIsSelectionWithinTable(editor),
      }
    : { isNestedTable: false, isSelectionWithinTable: false, numberOfColumns: 0, numberOfRows: 0 };

  return {
    canRemoveParagraph: !isLastBlockInAncestor && hasParagraphAboveSelection,
    insertParagraphAt: insertParagraphEntry?.[1] ?? null,
    isInTable: tableState.isSelectionWithinTable,
    isListItemSelected,
    isNestedTable: tableState.isNestedTable,
    numberOfColumns: tableState.numberOfColumns,
    numberOfRows: tableState.numberOfRows,
    showRemoveParagraph: Boolean(removeParagraphAncestorEntry),
  };
}
